/**
 * Stats Overview Component
 * Displays statistics and analytics for partner access
 */

import React, { useState, useEffect } from 'react';
import { KeyRound, ClipboardList, BarChart3 } from 'lucide-react';
import { partnerAccessApi } from '../../services/partnerAccessApi';
import { PartnerAccessStatsResponse } from '../../types/partnerAccess';

const StatsOverview: React.FC = () => {
  const [stats, setStats] = useState<PartnerAccessStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await partnerAccessApi.getStats();
      setStats(data);
    } catch (err: any) {
      console.error('Error loading stats:', err);
      setError('Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={styles.loading}>Lade Statistiken...</div>;
  }

  if (error || !stats) {
    return <div style={styles.error}>{error || 'Keine Daten verfügbar'}</div>;
  }

  return (
    <div style={styles.container}>
      {/* Overview Cards */}
      <div style={styles.grid}>
        <div style={styles.card}>
          <div style={styles.cardIcon}><KeyRound size={24} /></div>
          <div style={styles.cardContent}>
            <div style={styles.cardValue}>{stats.totalTokens}</div>
            <div style={styles.cardLabel}>Gesamt Tokens</div>
            <div style={styles.cardSubtext}>
              {stats.activeTokens} aktiv, {stats.revokedTokens} widerrufen, {stats.expiredTokens} abgelaufen
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardIcon}><ClipboardList size={24} /></div>
          <div style={styles.cardContent}>
            <div style={styles.cardValue}>{stats.totalAssignments}</div>
            <div style={styles.cardLabel}>Gesamt Zuweisungen</div>
            <div style={styles.cardSubtext}>
              {stats.activeAssignments} aktiv
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardIcon}><BarChart3 size={24} /></div>
          <div style={styles.cardContent}>
            <div style={styles.cardValue}>{stats.totalApiCalls.toLocaleString()}</div>
            <div style={styles.cardLabel}>API-Aufrufe gesamt</div>
            <div style={styles.cardSubtext}>
              {stats.apiCallsLast24h} in 24h, {stats.apiCallsLast7d} in 7d
            </div>
          </div>
        </div>
      </div>

      {/* Top Partners */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Top Partner nach Nutzung</h3>
        {stats.topPartners.length === 0 ? (
          <div style={styles.emptyState}>Keine Daten verfügbar</div>
        ) : (
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Partner</th>
                  <th style={styles.th}>Tokens</th>
                  <th style={styles.th}>Zuweisungen</th>
                  <th style={styles.th}>API-Aufrufe</th>
                  <th style={styles.th}>Zuletzt verwendet</th>
                </tr>
              </thead>
              <tbody>
                {stats.topPartners.map(partner => (
                  <tr key={partner.partnerId} style={styles.tr}>
                    <td style={styles.td}>
                      <strong>{partner.partnerName}</strong>
                    </td>
                    <td style={styles.td}>{partner.tokenCount}</td>
                    <td style={styles.td}>{partner.assignmentCount}</td>
                    <td style={styles.td}>{partner.apiCallCount.toLocaleString()}</td>
                    <td style={styles.td}>
                      {partner.lastUsed
                        ? new Date(partner.lastUsed).toLocaleString('de-DE')
                        : 'Nie'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Letzte Aktivitäten</h3>
        {stats.recentActivity.length === 0 ? (
          <div style={styles.emptyState}>Keine Aktivitäten</div>
        ) : (
          <div style={styles.activityList}>
            {stats.recentActivity.slice(0, 10).map(activity => (
              <div key={activity.id} style={styles.activityItem}>
                <div style={styles.activityHeader}>
                  <span style={styles.activityPartner}>{activity.partnerName}</span>
                  <span style={styles.activityTime}>
                    {new Date(activity.timestamp).toLocaleString('de-DE')}
                  </span>
                </div>
                <div style={styles.activityDetails}>
                  <span style={styles.activityAction}>{activity.action}</span>
                  <span style={styles.activityEndpoint}>{activity.endpoint}</span>
                  <span style={{
                    ...styles.activityStatus,
                    color: activity.responseStatus < 400 ? '#28a745' : '#dc3545'
                  }}>
                    {activity.responseStatus}
                  </span>
                  <span style={styles.activityDuration}>{activity.durationMs}ms</span>
                </div>
                {activity.errorMessage && (
                  <div style={styles.activityError}>{activity.errorMessage}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2rem',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    fontSize: '1.2rem',
    color: '#6c757d',
  },
  error: {
    textAlign: 'center',
    padding: '3rem',
    fontSize: '1.2rem',
    color: '#dc3545',
    backgroundColor: '#f8d7da',
    borderRadius: '12px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '1.5rem',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    display: 'flex',
    gap: '1rem',
  },
  cardIcon: {
    fontSize: '2.5rem',
  },
  cardContent: {
    flex: 1,
  },
  cardValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  cardLabel: {
    fontSize: '0.9rem',
    color: '#6c757d',
    marginTop: '0.25rem',
  },
  cardSubtext: {
    fontSize: '0.85rem',
    color: '#6c757d',
    marginTop: '0.5rem',
  },
  section: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#2c3e50',
    marginBottom: '1rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: '2rem',
    color: '#6c757d',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '0.75rem',
    textAlign: 'left',
    borderBottom: '2px solid #e9ecef',
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#495057',
    backgroundColor: '#f8f9fa',
  },
  tr: {
    borderBottom: '1px solid #e9ecef',
  },
  td: {
    padding: '0.75rem',
    fontSize: '0.9rem',
    color: '#495057',
  },
  activityList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  activityItem: {
    padding: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    borderLeft: '3px solid #007bff',
  },
  activityHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.5rem',
  },
  activityPartner: {
    fontWeight: 'bold',
    color: '#2c3e50',
  },
  activityTime: {
    fontSize: '0.85rem',
    color: '#6c757d',
  },
  activityDetails: {
    display: 'flex',
    gap: '1rem',
    fontSize: '0.85rem',
    color: '#495057',
  },
  activityAction: {
    fontWeight: '600',
  },
  activityEndpoint: {
    fontFamily: 'monospace',
    color: '#6c757d',
  },
  activityStatus: {
    fontWeight: '600',
  },
  activityDuration: {
    color: '#6c757d',
  },
  activityError: {
    marginTop: '0.5rem',
    padding: '0.5rem',
    backgroundColor: '#f8d7da',
    color: '#721c24',
    borderRadius: '4px',
    fontSize: '0.85rem',
  },
};

export default StatsOverview;

